import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  Receipt,
  Scan,
  FileText,
  DollarSign,
  Building,
  Hammer,
  CheckCircle,
  XCircle,
} from "lucide-react";
import axios from "axios";
import { MyDomain } from "../../../App";

const GOOGLE_API_KEY = "AIzaSyBy2ey-7Zos3KHKXurZlrMRr-aoVWxG-6Q";

// ── Currency options ──────────────────────────────────────────────────────────
const CURRENCIES = [
  { code: "ILS", symbol: "₪", name: "Israeli Shekel" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
];

// ── Reusable invoice field ────────────────────────────────────────────────────
function InvoiceField({
  label,
  value,
  onChange,
  placeholder = "",
  required = false,
  highlight = false,
  disabled = false,
  autoCalculated = false,
  type = "text",
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-semibold text-gray-600">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
        {autoCalculated && (
          <span className="text-green-500 font-normal italic ml-1">
            (Auto-calculated)
          </span>
        )}
      </label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full rounded-lg px-3 py-2.5 text-sm text-gray-900 outline-none border transition ${
          highlight
            ? "border-2 border-purple-400 bg-purple-50 focus:ring-1 focus:ring-purple-400"
            : disabled
              ? "border border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed"
              : "border border-gray-200 bg-white focus:border-purple-300 focus:ring-1 focus:ring-purple-200"
        }`}
      />
    </div>
  );
}

export default function BossCreateInvoice({
  visible,
  onClose,
  onSuccess,
  preselectedPlaceId = null,
}) {
  const { t } = useTranslation();
  const [allData, setAllDataState] = useState(null);
  const [userData, setUserData] = useState(null);

  const [selectedPlaceId, setSelectedPlaceId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);

  const [selectedCurrency, setSelectedCurrency] = useState("ILS");

  // Invoice fields
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [businessNumber, setBusinessNumber] = useState("");
  const [subtotal, setSubtotal] = useState("");
  const [taxAmount, setTaxAmount] = useState("");
  const [taxRate, setTaxRate] = useState("");
  const [totalAmount, setTotalAmount] = useState("");
  const [note, setNote] = useState("");

  const fileInputRef = useRef(null);

  useEffect(() => {
    const data = localStorage.getItem("allData");
    const user = localStorage.getItem("user");
    if (data) setAllDataState(JSON.parse(data));
    if (user) setUserData(JSON.parse(user));
  }, []);

  const getTodayDate = () => {
    const today = new Date();
    return `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;
  };

  // Init when modal opens
  useEffect(() => {
    if (visible) {
      setInvoiceDate(getTodayDate());
      setSelectedPlaceId(preselectedPlaceId);
      setSelectedCurrency("ILS");
    }
  }, [visible, preselectedPlaceId]);

  // Auto-calc subtotal and tax when total+rate change
  useEffect(() => {
    if (totalAmount && taxRate) {
      const total = parseFloat(totalAmount.replace(/[^\d.]/g, "")) || 0;
      const rate = parseFloat(taxRate.replace(/[^\d.]/g, "")) || 0;
      if (total > 0 && rate > 0) {
        const calcSubtotal = total / (1 + rate / 100);
        const calcTax = total - calcSubtotal;
        setSubtotal(calcSubtotal.toFixed(2));
        setTaxAmount(calcTax.toFixed(2));
      }
    }
  }, [totalAmount, taxRate]);

  const handleClose = () => {
    setSelectedPlaceId(null);
    setSelectedCurrency("ILS");
    setImageFile(null);
    setImagePreview(null);
    setInvoiceNumber("");
    setInvoiceDate("");
    setVendorName("");
    setBusinessType("");
    setBusinessNumber("");
    setSubtotal("");
    setTaxAmount("");
    setTaxRate("");
    setTotalAmount("");
    setNote("");
    onClose();
  };

  // ── Image selection ───────────────────────────────────────────────────────
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    processInvoice(file);
    e.target.value = "";
  };

  // ── Google Vision OCR ─────────────────────────────────────────────────────
  const processInvoice = async (file) => {
    setIsScanning(true);
    try {
      const base64 = await fileToBase64(file);
      await analyzeInvoice(base64);
    } catch (error) {
      console.error("❌ Error processing invoice:", error);
      alert("Failed to process invoice: " + (error.message || "Unknown error"));
    } finally {
      setIsScanning(false);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const analyzeInvoice = async (base64) => {
    const response = await fetch(
      `https://vision.googleapis.com/v1/images:annotate?key=${GOOGLE_API_KEY}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          requests: [
            {
              image: { content: base64 },
              features: [
                {
                  type: "DOCUMENT_TEXT_DETECTION",
                  maxResults: 1,
                  model: "builtin/latest",
                },
              ],
              imageContext: {
                languageHints: ["he", "iw", "he-IL", "ar", "en"],
                textDetectionParams: {
                  enableTextDetectionConfidenceScore: true,
                },
              },
            },
          ],
        }),
      },
    );

    if (!response.ok) throw new Error(`API returned status ${response.status}`);
    const data = await response.json();
    if (data.responses?.[0]?.error) {
      alert(data.responses[0].error.message);
      return;
    }
    const fullText = data.responses?.[0]?.fullTextAnnotation?.text || "";
    if (!fullText.trim()) {
      alert("No text found in the image. Please try a clearer photo.");
      return;
    }
    extractInvoiceData(fullText);
  };

  // ── OCR extraction ────────────────────────────────────────────────────────
  const extractInvoiceData = (text) => {
    const normalized = text.replace(/\s+/g, " ").replace(/,/g, ".").trim();
    const notFound = "לא נמצא";

    const invoiceNum = extractInvoiceNumber(normalized);
    const dateStr = extractDate(normalized);
    const vendor = extractVendorEnhanced(normalized);
    const businessInfo = extractBusinessInfoEnhanced(normalized);
    const amounts = extractAmountsEnhanced(normalized);

    setInvoiceNumber(invoiceNum !== notFound ? invoiceNum : "");
    setInvoiceDate(dateStr !== notFound ? dateStr : "");
    setVendorName(vendor !== notFound ? vendor : "");
    setBusinessType(
      businessInfo.businessType !== notFound ? businessInfo.businessType : "",
    );
    setBusinessNumber(
      businessInfo.businessNumber !== notFound
        ? businessInfo.businessNumber
        : "",
    );
    setSubtotal(amounts.subtotal !== notFound ? amounts.subtotal : "");
    setTaxAmount(amounts.taxAmount !== notFound ? amounts.taxAmount : "");
    setTaxRate(amounts.taxRate !== notFound ? amounts.taxRate : "");
    setTotalAmount(amounts.totalAmount !== notFound ? amounts.totalAmount : "");

    // Auto-detect currency from scanned text
    if (text.includes("$") || text.toUpperCase().includes("USD"))
      setSelectedCurrency("USD");
    else if (text.includes("€") || text.toUpperCase().includes("EUR"))
      setSelectedCurrency("EUR");
    else if (text.includes("£") || text.toUpperCase().includes("GBP"))
      setSelectedCurrency("GBP");
    else setSelectedCurrency("ILS");
  };

  const extractInvoiceNumber = (text) => {
    const patterns = [
      /(?:קבלה|חשבונית|חשבונית מס|חשבונית מס['\׳״"]?\s*קבלה)\s*(?:מס['\׳״"]?|מספר|מ["\']|#|:)?\s*[:.]?\s*([\w\-\/]+\d+)/i,
      /(?:מס['\׳״"]?\s*חשבונית|מס['\׳״"]?\s*קבלה)\s*[:.]?\s*([\w\-\/]+\d+)/i,
      /(?:receipt|invoice)\s*(?:no|number|#)?\s*[:.]?\s*([\w\-\/]+\d+)/i,
      /(?:קבלה|חשבונית)\s*[:.]?\s*(\d+[\w\-\/]*)/i,
    ];
    for (const p of patterns) {
      const m = text.match(p);
      if (m?.[1]) return m[1].trim();
    }
    return "לא נמצא";
  };

  const extractDate = (text) => {
    const patterns = [
      /(?:תאריך|date|תאריך הפקה|תאריך הנפקה)\s*[:.]?\s*(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4})/i,
      /(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{4})/,
    ];
    for (const line of text.split("\n").slice(0, 20))
      for (const p of patterns) {
        const m = line.match(p);
        if (m?.[1]) return m[1];
      }
    return "לא נמצא";
  };

  const extractVendorEnhanced = (text) => {
    const lines = text
      .split("\n")
      .map((l) => l.trim())
      .filter((l) => l.length > 2 && l.length < 100);
    const skip = [
      /^\d+$/,
      /^(receipt|invoice|קבלה|חשבונית)/i,
      /^(tax|vat|מע[״"'׳]?מ)/i,
      /^\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}$/,
    ];
    for (let i = 0; i < Math.min(5, lines.length); i++) {
      const line = lines[i];
      if (skip.some((r) => r.test(line))) continue;
      if (/[\u0590-\u05FF]/.test(line) || /[a-zA-Z]/.test(line))
        return line.substring(0, 60).trim();
    }
    return "לא נמצא";
  };

  const extractBusinessInfoEnhanced = (text) => {
    if (/בע[״"'׳]?מ/i.test(text)) {
      const numberPatterns = [
        /(?:ח\.פ|ח[\.״"']?\s*פ[\.״"']?)\s*[:.]?\s*([\d\-\/]{7,})/i,
        /(?:מס['\׳״"]?\s*עוסק)\s*[:.]?\s*([\d\-\/]{7,})/i,
      ];
      for (const p of numberPatterns) {
        const m = text.match(p);
        if (m?.[1])
          return { businessNumber: m[1].trim(), businessType: "חברה בע״מ" };
      }
      return { businessNumber: "לא נמצא", businessType: "חברה בע״מ" };
    }
    return { businessNumber: "לא נמצא", businessType: "לא נמצא" };
  };

  const extractAmountsEnhanced = (text) => {
    let totalAmount = "לא נמצא",
      taxAmount = "לא נמצא",
      taxRate = "לא נמצא",
      subtotal = "לא נמצא";
    const rateM = text.match(/(\d+)\s*%\s*מע[״"'׳]?מ/i);
    if (rateM?.[1]) taxRate = rateM[1] + "%";
    for (const p of [
      /(?:סה[״"'׳]?כ\s*לתשלום|לתשלום|סה[״"'׳]?כ)\s*[:.]?\s*[₪$€£]?\s*(\d+[.,]\d{1,2})/i,
      /(?:total)\s*[:.]?\s*[₪$€£]?\s*(\d+[.,]\d{1,2})/i,
    ]) {
      const m = text.match(p);
      if (m?.[1]) {
        totalAmount = m[1].replace(",", ".");
        break;
      }
    }
    for (const p of [
      /(?:מע[״"'׳]?מ)\s*[:.]?\s*[₪$€£]?\s*(\d+[.,]\d{1,2})/i,
      /(?:vat|tax)\s*[:.]?\s*[₪$€£]?\s*(\d+[.,]\d{1,2})/i,
    ]) {
      const m = text.match(p);
      if (m?.[1]) {
        taxAmount = m[1].replace(",", ".");
        break;
      }
    }
    for (const p of [
      /(?:חייב במע[״"'׳]?מ|לפני מע[״"'׳]?מ)\s*[:.]?\s*[₪$€£]?\s*(\d+[.,]\d{1,2})/i,
      /(?:subtotal)\s*[:.]?\s*[₪$€£]?\s*(\d+[.,]\d{1,2})/i,
    ]) {
      const m = text.match(p);
      if (m?.[1]) {
        subtotal = m[1].replace(",", ".");
        break;
      }
    }
    const total = parseFloat(totalAmount),
      tax = parseFloat(taxAmount),
      sub = parseFloat(subtotal);
    if (!isNaN(total) && !isNaN(tax) && subtotal === "לא נמצא")
      subtotal = (total - tax).toFixed(2);
    if (!isNaN(total) && !isNaN(sub) && taxAmount === "לא נמצא")
      taxAmount = (total - sub).toFixed(2);
    return { totalAmount, taxAmount, taxRate, subtotal };
  };

  // ── Submit — invoice ONLY, no expense ─────────────────────────────────────
  const handleSubmit = async () => {
    if (!vendorName?.trim()) {
      alert(t("vendorName"));
      return;
    }
    if (!invoiceNumber?.trim()) {
      alert(t("invoiceNumber"));
      return;
    }
    if (!invoiceDate?.trim()) {
      alert(t("invoiceDate"));
      return;
    }
    if (!totalAmount?.trim()) {
      alert(t("totalAmount"));
      return;
    }
    if (!userData) {
      alert(t("userDataNotFound"));
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append(
        "UserId",
        userData.type === 0 ? userData.id : userData.workWith,
      );
      formData.append("PlaceId", selectedPlaceId || "");
      formData.append("InvoiceNumber", invoiceNumber.trim());
      formData.append("InvoiceDate", invoiceDate.trim());
      formData.append("VendorName", vendorName.trim());
      formData.append("BusinessType", businessType.trim() || "");
      formData.append("BusinessNumber", businessNumber.trim() || "");
      formData.append("SubTotal", subtotal || "0");
      formData.append("TaxAmount", taxAmount || "0");
      formData.append("TaxRate", taxRate.replace("%", "").trim() || "0");
      // Send clean number — no currency symbol — backend stores it as-is
      formData.append(
        "TotalAmount",
        String(totalAmount || "0").replace(/[^0-9.]/g, ""),
      );
      // Always send explicit ISO currency code so backend never has to guess
      formData.append("Currency", selectedCurrency);
      formData.append("Note", note.trim());
      if (imageFile) formData.append("file", imageFile);

      const response = await axios.post(
        `${MyDomain}Expenses/CreateExpenseWithInvoice`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      if (response.status === 200) {
        handleClose();
        if (onSuccess) onSuccess(response.data.invoice || response.data);
      }
    } catch (error) {
      console.error("❌ Error:", error.response?.data || error.message);
      alert(error.response?.data?.message || t("error"));
    } finally {
      setIsLoading(false);
    }
  };

  if (!visible || !allData) return null;

  const activeCurrency =
    CURRENCIES.find((c) => c.code === selectedCurrency) || CURRENCIES[0];

  return (
    <div
      className="fixed inset-0 flex items-end sm:items-center justify-center z-50"
      style={{
        background: "rgba(241,245,249,0.75)",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-2xl max-h-[92vh] flex flex-col animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="border-b border-gray-100 px-5 pt-3 pb-4 rounded-t-3xl">
          <div className="w-9 h-1 bg-gray-300 rounded-full mx-auto mb-4 sm:hidden" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Receipt className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">{t("createInvoice")}</h2>
                <p className="text-sm text-gray-500">
                  {isScanning
                    ? t("loading")
                    : t("invoiceDetails")}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="w-10 h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* ── Scrollable form ── */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {/* Optional image scanner */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("invoiceDetails")}{" "}
              <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageChange}
            />
            {isScanning ? (
              <div className="flex flex-col items-center justify-center py-12 bg-purple-50 rounded-xl border-2 border-purple-200">
                <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin mb-3" />
                <p className="text-sm font-semibold text-purple-700">
                  {t("loading")}
                </p>
              </div>
            ) : imagePreview ? (
              <div className="relative rounded-xl overflow-hidden border-2 border-purple-400">
                <img
                  src={imagePreview}
                  alt="Invoice"
                  className="w-full h-52 object-cover"
                />
                <button
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview(null);
                  }}
                  className="absolute top-3 right-3 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-red-50 transition"
                >
                  <XCircle className="w-5 h-5 text-red-500" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex flex-col items-center justify-center gap-3 py-10 rounded-xl border-2 border-dashed border-purple-300 bg-purple-50 hover:bg-purple-100 transition"
              >
                <Scan className="w-9 h-9 text-purple-400" />
                <p className="text-sm font-bold text-gray-700">
                  {t("invoiceDetails")}
                </p>
                <p className="text-xs text-gray-400">
                  {t("note")}
                </p>
              </button>
            )}
          </div>

          {/* Invoice details */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-4">
            <div className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-500" />
              <h3 className="text-sm font-bold text-gray-700">
                {t("invoiceDetails")}
              </h3>
            </div>

            <InvoiceField
              label={t("invoiceNumber")}
              value={invoiceNumber}
              onChange={setInvoiceNumber}
              placeholder="INV-001"
              required
            />
            <InvoiceField
              label={t("invoiceDate")}
              value={invoiceDate}
              onChange={setInvoiceDate}
              placeholder="DD/MM/YYYY"
              required
            />
            <InvoiceField
              label={t("vendorName")}
              value={vendorName}
              onChange={setVendorName}
              placeholder="Company Name"
              required
            />
            <InvoiceField
              label={t("businessNumber")}
              value={businessType}
              onChange={setBusinessType}
              placeholder="חברה בע״מ"
            />
            <InvoiceField
              label={t("businessNumber")}
              value={businessNumber}
              onChange={setBusinessNumber}
              placeholder="515123456"
              type="number"
            />

            <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
              <DollarSign className="w-5 h-5 text-green-500" />
              <h3 className="text-sm font-bold text-gray-700">{t("totalAmount")}</h3>
            </div>

            {/* Currency selector */}
            <div className="space-y-2">
              <label className="block text-xs font-semibold text-gray-600">
                Currency
              </label>
              <div className="flex gap-2">
                {CURRENCIES.map((c) => (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => setSelectedCurrency(c.code)}
                    className={`flex-1 flex flex-col items-center py-2.5 rounded-xl border-2 transition ${
                      selectedCurrency === c.code
                        ? "border-purple-500 bg-purple-50"
                        : "border-gray-200 bg-white hover:border-gray-300"
                    }`}
                  >
                    <span
                      className={`text-lg font-bold ${selectedCurrency === c.code ? "text-purple-600" : "text-gray-500"}`}
                    >
                      {c.symbol}
                    </span>
                    <span
                      className={`text-[10px] font-semibold mt-0.5 ${selectedCurrency === c.code ? "text-purple-500" : "text-gray-400"}`}
                    >
                      {c.code}
                    </span>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-gray-400 pl-1">
                {activeCurrency.symbol} — {activeCurrency.name} · Auto-detected
                from scan, or select manually
              </p>
            </div>

            <InvoiceField
              label={`${t("totalAmount")} (${activeCurrency.symbol})`}
              value={totalAmount}
              onChange={setTotalAmount}
              placeholder="0.00"
              type="number"
              highlight
              required
            />
            <InvoiceField
              label={t("taxRate")}
              value={taxRate}
              onChange={setTaxRate}
              placeholder="18%"
            />
            <InvoiceField
              label={t("subTotal")}
              value={subtotal}
              onChange={setSubtotal}
              placeholder="0.00"
              disabled
              autoCalculated
            />
            <InvoiceField
              label={t("taxAmount")}
              value={taxAmount}
              onChange={setTaxAmount}
              placeholder="0.00"
              disabled
              autoCalculated
            />
          </div>

          {/* Place selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("place")}
            </label>
            {(allData.myPlaces || []).length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                <Building className="w-10 h-10 text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">{t("noData")}</p>
              </div>
            ) : (
              <div className="overflow-x-auto pb-1 hide-scrollbar">
                <div className="flex gap-3" style={{ width: "max-content" }}>
                  {(allData.myPlaces || []).map((place) => {
                    const isActive = selectedPlaceId === place.id;
                    return (
                      <button
                        key={place.id}
                        onClick={() =>
                          setSelectedPlaceId(isActive ? null : place.id)
                        }
                        className={`relative w-32 flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition ${
                          isActive
                            ? "bg-purple-50 border-purple-500"
                            : "bg-gray-50 border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        {isActive && (
                          <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-purple-500" />
                        )}
                        <div
                          className={`w-12 h-12 rounded-full flex items-center justify-center ${isActive ? "bg-purple-200" : "bg-gray-200"}`}
                        >
                          {place.placeType === 1 ? (
                            <Building
                              className={`w-6 h-6 ${isActive ? "text-purple-600" : "text-gray-500"}`}
                            />
                          ) : (
                            <Hammer
                              className={`w-6 h-6 ${isActive ? "text-purple-600" : "text-gray-500"}`}
                            />
                          )}
                        </div>
                        <p
                          className={`text-xs font-semibold text-center leading-tight min-h-[2rem] ${isActive ? "text-purple-700" : "text-gray-700"}`}
                        >
                          {place.place}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {t("note")}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t("note")}
              rows={3}
              maxLength={200}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-gray-900 outline-none resize-none focus:border-purple-300 transition text-sm"
            />
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="border-t border-gray-100 px-5 py-4">
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className={`w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold transition ${
              isLoading
                ? "bg-gray-300 text-gray-400 cursor-not-allowed"
                : "bg-purple-600 hover:bg-purple-700 text-white shadow-md"
            }`}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>{t("loading")}</span>
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                <span>{t("createInvoice")}</span>
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slide-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        input[type="number"]::-webkit-inner-spin-button,
        input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
}
